//@Version: 1.0.1

import { Inject, Injectable, Injector, OnDestroy, Optional } from '@angular/core';
import { DefaultService, DPSAPI } from 'build/openapi/shared';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap,  map, tap } from 'rxjs/operators';
import { ErrorOpenApiService } from 'src/app/shared/services/errorHandler/error-openapi.service'
import { DESIGNFILE, ERROR, IConfig, IIdentifierDesign, IProjectConfig, IVersion, IVersionData, ResponseAffected, ResponseTranslateConfig } from 'build/project/openapi-cast/shared/shared-openapi-cast.model';
import { ResponseVersion, ResponseDesignVersion, ResponseDesignColor, ResponseDesignTypography, ResponseTranslateText} from 'build/project/openapi-cast/shared/shared-openapi-cast.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { JsonHelpersService } from 'src/app/shared/services/helpers/json-helpers.service';

export * from 'build/project/openapi-cast/shared/shared-openapi-cast.model';

UntilDestroy()
@Injectable({
   providedIn: 'root'
})

export class SharedOpenapiCastService implements OnDestroy  {

   private isConnected_ = new BehaviorSubject<ERROR>(ERROR.START);
   public isConnected$ = this.isConnected_.asObservable();
   
   private version_: string = ""
   private versionDesign_: string = "";
   private versionBackend_: string = ""
   private path_: string = "";
    
   public config!: IConfig;
   public versionList!: IVersion;
  
   get versionBackend (): string {
      return this.versionBackend_;
   }
    
   get version (): string {
      return this.version_;
   }
    
   get versionDesign (): string {
      return this.versionDesign_;
   }
    
   get checkConnection (): boolean {
      return this.isConnected_.value !== ERROR.CONNECT ? false : true;
   }
    
   constructor (public sDefault: DefaultService, private injector: Injector, private httpClient: HttpClient, @Optional() @Inject('PATH') p: string, private sJsonHelpers: JsonHelpersService) {
      this.isConnected_.next(ERROR.START);
      this.path_ = p || './assets/';
   
      const subscription = this.isConnected$.subscribe(value => {
         const sErrorOpenApi = (this.injector).get(ErrorOpenApiService);
         switch (value) {
            case ERROR.UUID:
               sErrorOpenApi.routingCode('910');
               subscription.unsubscribe();
               return;
            case ERROR.BACKEND:
               sErrorOpenApi.routingCode('Backend ist dem Server nicht bekannt');
               subscription.unsubscribe();
               return;
            case ERROR.GDIX:
               sErrorOpenApi.routingCode('Backend-Error: connect_error in send:');
               subscription.unsubscribe();
               return;
            case ERROR.PROJECT:
               sErrorOpenApi.routingCode('911');
               subscription.unsubscribe();
               return;
            case ERROR.DPSAPI:
               sErrorOpenApi.routingCode('912');
               subscription.unsubscribe();
               return;
            case ERROR.SGINALR:
               sErrorOpenApi.routingCode('913');
               subscription.unsubscribe();
               return;
            case ERROR.IGNORE:
               subscription.unsubscribe();
               return;
            case ERROR.CONNECT:
               subscription.unsubscribe();
               return;
            default: return;
         }
      });
   }
   
   ngOnDestroy (): void {
      this.version_ = "";
      this.versionDesign_ = "";
      this.versionBackend_ = ""
      this.isConnected_.next(ERROR.START);
      this.path_ = ""; 
   }
    
   private startConnection (): Observable<boolean> {
      if (this.sDefault.isConnected()) return of(true);
      return from(this.sDefault.Connected()).pipe(
         concatMap(value => of(this.sDefault.isConnected()))
      );
   }
        
   public setIsConnected (): Observable<boolean> {
      return this.checkUuid().pipe(
         concatMap(uuidDpsApi => this.checkDpsApi(uuidDpsApi)),
         concatMap(signalRResposne => this.checkSignalConnection(signalRResposne)),
         concatMap(backendResponse => this.checkBackendVersion(backendResponse)),
         concatMap(gdixResponse => this.checkGdIxVersion(gdixResponse)),
         concatMap(projectResponse => this.checkDesignVersion(projectResponse)),
         tap(value => this.isConnected_.next(value)),
         concatMap(() => of(true))
      );
   }
    
   private checkUuid (): Observable<boolean | ERROR> {
      if (!this.isUuid()) return of(ERROR.UUID);
      return of(this.sDefault.isDpsApi());
   }
    
   private checkDpsApi (result: boolean | ERROR): Observable<boolean | ERROR> {
      if (typeof result == 'number') return of(result);
      else if (typeof result == 'undefined') return of(ERROR.DPSAPI);
      else if (! result ) return of(ERROR.DPSAPI);
      return this.startConnection();
   }
       
   private checkSignalConnection (result: boolean | ERROR): Observable<string | ERROR>  {
      if (typeof result == 'number') return of(result);
      else if (typeof result == 'undefined') return of(ERROR.SGINALR);
      else if (!result) return of(ERROR.SGINALR);
      else return this.getBackendVersion();
   }
     
   private isUuid (): boolean {
      return this.sDefault.isUuid();
   }
    
   private getGdixVersion (): Observable<ResponseVersion> {
      return this.sDefault.dPSVersionV1Post({}, {}).pipe(
         map(value => typeof value == 'string'? {} as ResponseVersion : <ResponseVersion>value)
      );
   }
    
   private getDesignVersion (): Observable<ResponseDesignVersion> {
      return this.sDefault.dPSOKDesignV1VersionGet({}, {}).pipe(
         map(value => <ResponseDesignVersion>value)
      );
   }
      
   private getBackendVersion (): Observable<string> {
      return this.sDefault.getBackendVersion().pipe(map(value => <string>value));
   }
    
   private checkBackendVersion(result: string | ERROR): Observable<ResponseVersion | ERROR> {
      if (typeof result == 'number') return of(result);
      else if (!result || result.length == 0 ) return of(ERROR.BACKEND);
      else if (result == 'ERROR' ) return of(ERROR.BACKEND);
      else this.versionBackend_ = result;
      console.info('Backendversion', this.versionBackend_);
      return this.getGdixVersion();
   }
    
   private checkGdIxVersion (result: ResponseVersion | ERROR | string): Observable<ResponseDesignVersion | ERROR> {
      if (typeof result == 'number') return of(result);
      else if (typeof result == 'string' && result == 'ERROR') return of(ERROR.IGNORE);
      else if (typeof result == 'undefined') return of(ERROR.GDIX);
      else if (Object.keys(result).length == 0) return of(ERROR.GDIX);
      else if ((result as ResponseVersion).version && (result as ResponseVersion).version == '') return of(ERROR.GDIX);
      else this.version_ = (result as ResponseVersion).version!;
      console.info('GdIxversion', this.version_);
      return this.getDesignVersion();
   }
    
   private checkDesignVersion (result: ResponseDesignVersion | ERROR | string): Observable<ERROR> {
      if (typeof result == 'number') return of(result);
      else if (typeof result == 'string' && result == 'ERROR') return of(ERROR.IGNORE);
      else if (typeof result == 'undefined') return of(ERROR.PROJECT);
      else if (Object.keys(result).length == 0) return of(ERROR.PROJECT);
      else if ((result as ResponseDesignVersion).version && (result as ResponseDesignVersion).version == '') return of(ERROR.GDIX);
      else this.versionDesign_ = (result as ResponseDesignVersion).version!;
      console.info('DesignVersion', this.versionDesign_);
      return of(ERROR.CONNECT);
   }

   public loadDefault (filename: string): Observable<object> {
      return this.httpClient.get<object>(`./assets/defaultDesignData/${filename}.json`).pipe(
         catchError(() => of([]))
      );
   }

   public loadTranslateConfig (newProjectname: string = '', option: string, defaultValue: boolean = false): Observable<object> {
      return defaultValue ? this.loadDefault(DESIGNFILE.CONFIG) : this.loadCSVFiles(newProjectname, option, DESIGNFILE.CONFIG);
   }


   public loadTranslateText (newProjectname: string = '', option = '-1', defaultValue: boolean = false): Observable<object> {
      return defaultValue ? this.loadDefault(DESIGNFILE.TEXTDEFAULT) : this.loadCSVFiles(newProjectname, option, DESIGNFILE.TEXT);
   }

   private loadCSVFiles (newProjectname: string = '', option: string, name: string): Observable<object> {
      const elementName = this.loadElemName(newProjectname);

      const query = name == DESIGNFILE.CONFIG ? this.sDefault.dPSDesignV2ExternCommunicationLoadConfigProjectnameVersionOptionGet({projectname: elementName, version: this.versionList.design, option: option, },{}).pipe(map(result => (typeof result == 'string' && result == DPSAPI.ERROR) ? [] as ResponseTranslateConfig[]  : result as ResponseTranslateConfig[]))
                        : this.sDefault.dPSDesignV2ExternCommunicationLoadTextProjectnameVersionOptionGet({projectname: elementName, version: this.versionList.design, option: option},{}).pipe(map(result => (typeof result == 'string' && result == DPSAPI.ERROR) ? [] as ResponseTranslateText[]  : result as ResponseTranslateText[]))
      return query.pipe(
         map(response => this.transformTranslateText(response)),
         catchError(() => this.loadDefault(name == DESIGNFILE.CONFIG ? DESIGNFILE.CONFIG : DESIGNFILE.TEXTDEFAULT))
      )
      
   }

   private transformTranslateText (translateTextList: ResponseTranslateText[] | ResponseTranslateConfig []): object {
      const result = translateTextList.map(element => {
         const key = element?.key;
         const obj = {} as { [key: string]: unknown };
         obj[key as keyof typeof obj] = element?.value;
         return obj;
      });
      return this.sJsonHelpers.unflatten(this.sJsonHelpers.arrayToObject(this.sJsonHelpers.flatten(result)));
   }

   public loadDesignColor (newProjectname: string = '', option = '-1'): Observable<ResponseDesignColor[]> {
      let identifier: IIdentifierDesign = {
         projectname: this.loadElemName(newProjectname),
         version:  this.versionList.design,
         option: option
      };
      return this.sDefault.dPSDesignV2ExternCommunicationLoadColorProjectnameVersionOptionGet(identifier, {}).pipe(
         map(result => (typeof result == 'string' && result == DPSAPI.ERROR) ? [] as ResponseDesignColor[]  : result as ResponseDesignColor[])
      );
   }

   public loadDesignTypography (newProjectname: string = '', option = '-1'): Observable<ResponseDesignTypography[]> {
      let identifier: IIdentifierDesign = {
         projectname: this.loadElemName(newProjectname),
         version:  this.versionList.design,
         option: option
      };

      return this.sDefault.dPSDesignV2ExternCommunicationLoadTypographyProjectnameVersionOptionGet(identifier, {}).pipe(
         map(result => (typeof result == 'string' && result == DPSAPI.ERROR) ? [] as ResponseDesignTypography[]  : result as ResponseDesignTypography[])
      );
   }


   public setConfigs(): Observable<boolean> {
      return  forkJoin([
         this.httpClient.get<IConfig>('./assets/building.json'),
         this.httpClient.get<IVersion>('./version.json')
      ]).pipe(
         map((item => {
            this.config = item[0];
            this.versionList = item[1];
            return true;
         })
      ));
   }

   private loadElemName (newProjectname: string = ''): string {
      return newProjectname == '' || newProjectname == undefined || (this.config[this.config.first] as IProjectConfig).name == newProjectname 
      ? (this.config[this.config.first] as IProjectConfig).name
      : (this.config[newProjectname] as IProjectConfig).name; 
   }

   /**
   * 
   * @returns 1: the same; 2: versionDesign is bigger than in version.json, 3: version is smaller than in version.json
   */
   public checkVersionDesgin (): number {
      const versionDesign: number = Number(this.versionDesign.replaceAll('v','').replaceAll('.',''));
      const versionFile: number = Number(this.versionList.design.replaceAll('v','').replaceAll('.',''));
      return versionDesign == versionFile ? 1 : versionDesign > versionFile ? 2 : versionDesign < versionFile ? 3 : 0;
   }
   
   /**
   * 
   * @param key CSV key
   * @param application shared, project oder Applicatons...
   * @retuns 1: the same; 2: versionProject is bigger than in version.json, 3: version is smaller than in version.json
   */
   public checkVersionCSV (key: string, application: string, translateVersion: string): number {
      const versionFile: number = Number((this.versionList[key] as IVersionData)[application].replaceAll('v','').replaceAll('.',''));
      const versionDesignService: number =  Number(translateVersion.replaceAll('v','').replaceAll('.',''));
      return versionDesignService == versionFile ? 1 : versionDesignService > versionFile ? 2 : versionDesignService < versionFile ? 3 : 0;
   }
   
   public checkAllAffected (affectedList: ResponseAffected | ResponseAffected []): boolean {
      return Array.isArray(affectedList) ? affectedList.filter(item => this.checkAffected(item)).length == affectedList.length : this.checkAffected(affectedList);
   }
   
   private checkAffected (item: ResponseAffected): boolean {
      return  item && item.affected && item.affected == 1 ? true : false;
   }
   
   public getConfigFile (): Observable<any> {
      return this.httpClient.get('./assets/config.json');
   }
}
